import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';

import InventoryService from '../../services/inventoryService';
import { maskVinName, showMsg } from '../../services/library';
import DealerService from '../../services/dealerService';
import NotesService from '../../services/notesService';
// import SettingService from '../../services/settingService';

const REQ_INVENTORY = 'inventory/';

export const fetchDealerChangeReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchDealerChangeReq`,
  async ({ dealer }, { rejectWithValue }) => {
    try {
      const [tableData, chartData] = await Promise.all([
        InventoryService.getTableData({ dealer_id: dealer?.value }),
        InventoryService.getChartData({ dealer_id: dealer?.value })
      ]);

      if (tableData.status || chartData.status) {
        let result = [];

        if (tableData?.data) {
          result = tableData.data.map(i => ({ ...i, inv_vin: maskVinName(null, i.inv_vin) }));
        }

        return {
          totalData: result,
          tableData: tableData?.data?.length > 0 ? tableData?.data[0] : {},
          chartData: chartData?.data?.length > 0 ? chartData?.data[0] : null
        };
      }
      const message = tableData?.errors || chartData?.errors;
      showMsg(message, 'error');
      return rejectWithValue(message);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchVehicleNotesReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchVehicleNotesReq`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await NotesService.getVehicleNotes(payload);
      if (response.status) {
        return { data: response.data, payload };
      }
      showMsg(response.errors, 'error');
      return rejectWithValue(response.errors);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchUpdateVehicleNoteReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchUpdateVehicleNoteReq`,
  async (payload, { rejectWithValue }) => {
    try {
      const response = await NotesService.updateVehicleNote(payload);
      if (response.status) {
        return true;
      }
      showMsg(response.errors, 'error');
      return rejectWithValue(response.errors);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchInventoryReportReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchInventoryReportReq`,
  async ({ dealer, date, invetoryReportFilterdArray }) => {
    try {
      const res = await InventoryService.reportInventory({
        dealer_id: dealer?.value,
        date,
        to_list: invetoryReportFilterdArray
      });
      if (res.status) {
        showMsg(res.message, 'success');
      } else {
        showMsg(res.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

export const fetchVdsReportReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchVdsReportReq`,
  async ({ dealer, date, inventoryVDSReportFilterdArray }) => {
    try {
      const res = await InventoryService.vdsReport({
        dealer_id: dealer?.value,
        date,
        to_list: inventoryVDSReportFilterdArray
      });
      if (res.status) {
        showMsg(res.message, 'success');
      } else {
        showMsg(res.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

export const fetchSendAgeBucketsReportReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchAgeBucketReportReq`,
  async data => {
    try {
      const res = await InventoryService.sendIndividualAgeBucketReport(data);
      if (res.status) {
        showMsg(res.message, 'success');
      } else {
        showMsg(res.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

// fetch Age Bucket report recipients
export const fetchAgeBucketReportRecipients = createAsyncThunk(
  `${REQ_INVENTORY}fetchAgeBucketReportRecipients`,
  async data => {
    try {
      const response = await DealerService.getAgeBucketReportRecipients(data);
      if (response.status) {
        const recipientsArray = response.data[0].dealer_age_bucket_report_recipients
          ? response.data[0].dealer_age_bucket_report_recipients.split(',')
          : [];

        return recipientsArray;
      } else {
        showMsg(response.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

// fetch photo report recipients
export const fetchReportRecipients = createAsyncThunk(`${REQ_INVENTORY}fetchReportRecipients`, async data => {
  try {
    const response = await DealerService.getReportRecipients(data);
    if (response.status) {
      const recipientsArray = response.data[0].dealer_reprec ? response.data[0].dealer_reprec.split(',') : [];

      return recipientsArray;
    } else {
      showMsg(response.errors, 'error');
    }
  } catch (error) {
    showMsg(error, 'error');
  }
});

// fetch photo report recipients
export const fetchVDSReportRecipients = createAsyncThunk(
  `${REQ_INVENTORY}fetchVDSReportRecipients`,
  async data => {
    try {
      const response = await DealerService.getVDSReportRecipients(data);
      if (response.status) {
        const recipientsArray = response.data[0].dealer_vds_report_recipients
          ? response.data[0].dealer_vds_report_recipients.split(',')
          : [];

        return recipientsArray;
      } else {
        showMsg(response.errors, 'error');
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

export const downloadLibPhotosReq = createAsyncThunk(
  `${REQ_INVENTORY}downloadLibPhotosReq`,
  async ({ dealer, vin, make, model }) => {
    try {
      const response = await InventoryService.downloadLibPhotos({
        dealer_id: dealer?.value,
        vin
      });
      if (response.type === 'application/json') {
        let reader = new FileReader();
        reader.onload = function () {
          showMsg(JSON.parse(this.result).errors[0], 'error');
        };
        reader.readAsText(response);
      } else {
        // download file
        const url = window.URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `(${make}-${model})${vin}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showMsg(error, 'error');
    }
  }
);

export const fetchSearchInvDataReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchSearchInvDataReq`,
  async (search, { rejectWithValue }) => {
    try {
      const response = await InventoryService.searchInv({ search });
      if (response.status) {
        return response.data;
      }
      showMsg(response.errors, 'error');
      return rejectWithValue(response.errors);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchDealerRatingReq = createAsyncThunk(
  `${REQ_INVENTORY}fetchDealerRatingReq`,
  async ({ dealers }, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getDealerRating({ dealers });
      if (response.status) {
        return response.data;
      }
      showMsg(response.errors, 'error');
      return rejectWithValue(response.errors);
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    sortValue: [
      { id: 'dms_days', desc: true },
      { id: 'inv_make', desc: true }
    ],
    isRRStatus: false,
    isIPacketStatus: false,
    invChartData: null,
    totalData: [],
    filteredRecords: [],
    chartLoaded: true,
    tableLoaded: true,
    dataFilter: 'Total',
    subFilter: 'all',
    invSearchBox: '',
    currentSearchValue: '',
    vdsTotal: 0,
    cpoTotal: 0,
    windowstickerTotal: 0,
    buyersguideTotal: 0,
    VDSFilter: false,
    cpoFilter: false,
    windowstickerFilter: false,
    buyersguideFilter: false,
    notesFilter: '',
    tableSummary: {
      total: 0,
      published: 0,
      photosPending: 0,
      editPending: 0,
      publishedVideo: 0,
      interiorCount: 0,
      exteriorCount: 0
    },
    notesModal: false,
    vehicleNotesData: [],
    isAllowVideo: 0,
    currentVehicleVin: '',
    currentVehicleType: '',
    viewer: 0,
    isOpenStickerModel: false,
    invStickerData: null,
    sticker_type: 0,
    isOpenGuideModel: false,
    guideData: null,
    labeltype: '',
    isOpenAddendumModel: false,
    isOpenAiCommentModel: false,
    toggleVdpTrendModal: false,
    addendumData: null,
    aiCommentData: '',
    isOpenStickerHistoryModel: false,
    invStickerHistoryData: [],
    isOpenRatingModel: false,
    isOpenCustWarrModel: false,
    custWarrData: null,
    customwarrantyform: 0,
    dealerState: null,
    recipients: [],
    checkedStatus: {},
    isAllSelected: true,
    vdsRecipients: [],
    checkedVdsStatus: {},
    isAllVdsSelected: true,
    ageBucketRecipients: [],
    checkedAgeBucketStatus: {},
    isAllAgeBucketSelected: true,
    // SearchInvComponent
    searchInvData: [],
    isSearchInvDataLoaded: false,
    isSearchInvLoading: false,
    dealerRating: [],
    isdealerRatingLoading: true,
    //Bucket
    bucketRange: 15,
    bucketActive: null,
    isLSPDealer: false,
    dealerBuyersGuideLanguage: 'en'
  },
  reducers: {
    updateCheckedStatus: {
      reducer(state, action) {
        const { email, checked } = action.payload;
        state.checkedStatus[email] = checked;
        // Update isAllSelected based on the current state
        const allChecked = Object.values(state.checkedStatus).every(status => status);
        state.isAllSelected = allChecked;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    updateVDSCheckedStatus: {
      reducer(state, action) {
        const { email, checked } = action.payload;
        state.checkedVdsStatus[email] = checked;
        // Update isAllVdsSelected based on the current state
        const allChecked = Object.values(state.checkedVdsStatus).every(status => status);
        state.isAllVdsSelected = allChecked;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    updateAgeBucketCheckedStatus: {
      reducer(state, action) {
        const { email, checked } = action.payload;
        state.checkedAgeBucketStatus[email] = checked;
        // Update isAllVdsSelected based on the current state
        const allChecked = Object.values(state.checkedAgeBucketStatus).every(status => status);
        state.isAllAgeBucketSelected = allChecked;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    toggleAgeBucketSelectAll: {
      reducer(state, action) {
        const { checked } = action.payload;
        state.isAllAgeBucketSelected = checked;
        state.checkedAgeBucketStatus = Object.keys(state.checkedAgeBucketStatus)?.reduce((acc, email) => {
          acc[email] = checked;
          return acc;
        }, {});
      },
      prepare(checked) {
        return { payload: { checked } };
      }
    },
    updateAgeBucketRecipients: {
      reducer(state, action) {
        state.ageBucketRecipients = action.payload || [];
        state.checkedAgeBucketStatus = action.payload?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
      },
      prepare(e) {
        return { payload: e };
      }
    },
    toggleSelectAll: {
      reducer(state, action) {
        const { checked } = action.payload; // Ensure payload has 'checked'
        state.isAllSelected = checked;
        state.checkedStatus = Object.keys(state.checkedStatus)?.reduce((acc, email) => {
          acc[email] = checked;
          return acc;
        }, {});
      },
      prepare(checked) {
        return { payload: { checked } };
      }
    },
    toggleVdsSelectAll: {
      reducer(state, action) {
        const { checked } = action.payload;
        state.isAllVdsSelected = checked;
        state.checkedVdsStatus = Object.keys(state.checkedVdsStatus)?.reduce((acc, email) => {
          acc[email] = checked;
          return acc;
        }, {});
      },
      prepare(checked) {
        return { payload: { checked } };
      }
    },
    updateRecipients: {
      reducer(state, action) {
        state.recipients = action.payload || [];
        state.checkedStatus = action.payload?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
      },
      prepare(e) {
        return { payload: e };
      }
    },
    updateVDSRecipients: {
      reducer(state, action) {
        state.vdsRecipients = action.payload || [];
        state.checkedVdsStatus = action.payload?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsRRStatus: {
      reducer(state, action) {
        state.isRRStatus = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsIPacketStatus: {
      reducer(state, action) {
        state.isIPacketStatus = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleChartData: {
      reducer(state, action) {
        state.invChartData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTotalData: {
      reducer(state, action) {
        state.totalData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilteredRecords: {
      reducer(state, action) {
        state.filteredRecords = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleChartLoaded: {
      reducer(state, action) {
        state.chartLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTableLoaded: {
      reducer(state, action) {
        state.tableLoaded = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDataFilter: {
      reducer(state, action) {
        state.dataFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSubFilter: {
      reducer(state, action) {
        state.subFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSearchBox: {
      reducer(state, action) {
        state.invSearchBox = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCurrentSearchValue: {
      reducer(state, action) {
        state.currentSearchValue = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVdsTotal: {
      reducer(state, action) {
        state.vdsTotal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCpoTotal: {
      reducer(state, action) {
        state.cpoTotal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleWindowstickerTotal: {
      reducer(state, action) {
        state.windowstickerTotal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBuyersguideTotal: {
      reducer(state, action) {
        state.buyersguideTotal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleSetVdsFilter: {
      reducer(state, action) {
        state.VDSFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCpoFilter: {
      reducer(state, action) {
        state.cpoFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleWindowstickerFilter: {
      reducer(state, action) {
        state.windowstickerFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleBuyersguideFilter: {
      reducer(state, action) {
        state.buyersguideFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleNotesFilter: {
      reducer(state, action) {
        state.notesFilter = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleTableSummary: {
      reducer(state, action) {
        state.tableSummary = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleNotesModal: {
      reducer(state, action) {
        state.notesModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleVehicleNotesData: {
      reducer(state, action) {
        state.vehicleNotesData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCurrentVehicleVin: {
      reducer(state, action) {
        state.currentVehicleVin = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCurrentVehicleType: {
      reducer(state, action) {
        state.currentVehicleType = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleViewer: {
      reducer(state, action) {
        state.viewer = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenStickerModel: {
      reducer(state, action) {
        state.isOpenStickerModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleInvStickerData: {
      reducer(state, action) {
        state.invStickerData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleStickerType: {
      reducer(state, action) {
        state.sticker_type = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenGuideModel: {
      reducer(state, action) {
        state.isOpenGuideModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleGuideData: {
      reducer(state, action) {
        state.guideData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleLabelType: {
      reducer(state, action) {
        state.labeltype = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenAddendumModel: {
      reducer(state, action) {
        state.isOpenAddendumModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenAiCommentModel: {
      reducer(state, action) {
        state.isOpenAiCommentModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleToggleVdpTrendModal: {
      reducer(state, action) {
        state.toggleVdpTrendModal = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleAddendumData: {
      reducer(state, action) {
        state.addendumData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleAiCommentData: {
      reducer(state, action) {
        state.aiCommentData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenStickerHistoryModel: {
      reducer(state, action) {
        state.isOpenStickerHistoryModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleInvStickerHistoryData: {
      reducer(state, action) {
        state.invStickerHistoryData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenRatingModel: {
      reducer(state, action) {
        state.isOpenRatingModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsOpenCustWarrModel: {
      reducer(state, action) {
        state.isOpenCustWarrModel = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleCustWarrData: {
      reducer(state, action) {
        state.custWarrData = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsCustomWarrantyForm: {
      reducer(state, action) {
        state.customwarrantyform = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDealerState: {
      reducer(state, action) {
        state.dealerState = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleDealerLSP: {
      reducer(state, action) {
        state.isLSPDealer = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleResetInvPage: {
      reducer(state) {
        state.vehicleNotesData = [];
        state.currentVehicleVin = '';
        state.currentVehicleType = '';
        state.isRRStatus = false;
        state.isIPacketStatus = false;
        state.chartLoaded = true;
        state.tableLoaded = true;
        state.invChartData = null;
        state.totalData = [];
        state.invSearchBox = '';
        state.filteredRecords = [];
        state.dataFilter = 'Total';
        state.subFilter = 'all';
        state.vdsTotal = 0;
        state.cpoTotal = 0;
        state.VDSFilter = false;
        state.cpoFilter = false;
        state.windowstickerFilter = false;
        state.buyersguideFilter = false;
        state.notesFilter = '';
        state.tableSummary = {
          total: 0,
          published: 0,
          preliminary: 0,
          photosPending: 0,
          editPending: 0,
          publishedVideo: 0,
          interiorCount: 0,
          exteriorCount: 0
        };
        state.viewer = 0;
        state.invStickerData = null;
        state.sticker_type = 0;
        state.guideData = null;
        state.labeltype = '';
        state.addendumData = null;
        state.aiCommentData = null;
        state.custWarrData = null;
        state.customwarrantyform = 0;
        state.dealerState = null;
        state.recipients = [];
        state.vdsRecipients = [];
        state.isLSPDealer = false;
        state.dealerBuyersGuideLanguage = 'en';
      },
      prepare() {
        return { payload: null };
      }
    },
    handleDealerRating: {
      reducer(state) {
        state.dealerRating = [];
        state.isdealerRatingLoading = true;
      },
      prepare() {
        return { payload: null };
      }
    },
    handleBucketRange: {
      reducer(state, action) {
        state.bucketRange = action.payload;
        state.subFilter = 'all';
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilterReset: {
      reducer(state) {
        state.dataFilter = 'Total';
        state.subFilter = 'all';
        state.VDSFilter = false;
        state.cpoFilter = false;
        state.windowstickerFilter = false;
        state.buyersguideFilter = false;
        state.notesFilter = '';
        state.filteredRecords = [];
        state.sortValue = [
          {
            id: 'dms_days',
            desc: true
          },
          { id: 'inv_make', desc: true }
        ];
      },
      prepare() {
        return { payload: null };
      }
    },
    handleSortValue: {
      reducer(state, action) {
        state.sortValue = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    }
  },
  extraReducers: builder => {
    builder
      //#region fetchDealerChangeReq
      .addCase(fetchDealerChangeReq.pending, state => {
        state.vehicleNotesData = [];
        state.isAllowVideo = 0;
        state.currentVehicleVin = '';
        state.currentVehicleType = '';
        state.isRRStatus = false;
        state.isIPacketStatus = false;
        state.chartLoaded = true;
        state.tableLoaded = true;
        state.invChartData = null;
        state.totalData = [];
        state.invSearchBox = '';
        state.vdsTotal = 0;
        state.cpoTotal = 0;
        state.windowstickerTotal = 0;
        state.windowstickerTotal = 0;
        state.tableSummary = {
          total: 0,
          published: 0,
          preliminary: 0,
          photosPending: 0,
          editPending: 0,
          publishedVideo: 0,
          interiorCount: 0,
          exteriorCount: 0
        };
        state.viewer = 0;
        state.invStickerData = null;
        state.sticker_type = 0;
        state.guideData = null;
        state.labeltype = '';
        state.addendumData = null;
        state.aiCommentData = null;
        state.custWarrData = null;
        state.customwarrantyform = 0;
        state.dealerState = null;
        state.isLSPDealer = false;
        state.dealerBuyersGuideLanguage = 'en';
      })
      .addCase(fetchDealerChangeReq.fulfilled, (state, action) => {
        const { currentSearchValue } = current(state);
        state.invSearchBox = currentSearchValue;

        const { tableData, chartData, totalData } = action.payload;
        state.totalData = totalData;
        state.invChartData = chartData;

        state.isAllowVideo = tableData?.isAllowVideo || 0;
        state.viewer = tableData?.dealer_viewer || 0;
        state.isRRStatus = !!tableData?.dealer_rapidrecon_id;
        state.isIPacketStatus = !!tableData?.dealer_ipacket_id;
        state.labeltype = tableData?.dealer_warranty_type || '';
        state.customwarrantyform = tableData?.dealer_customwarrantyform || 0;
        state.dealerState = tableData?.dealer_state || null;
        state.isLSPDealer = !!tableData?.dealer_is_lsp_bill;
        state.dealerBuyersGuideLanguage = tableData?.dealer_buyersguide_language || 'en';

        state.chartLoaded = false;
        state.currentSearchValue = '';
        if (totalData?.length === 0) {
          state.tableLoaded = false;
        }
      })
      .addCase(fetchDealerChangeReq.rejected, state => {
        state.tableLoaded = false;
        state.chartLoaded = false;
        state.totalData = [];
        state.invChartData = [];
        state.currentSearchValue = '';
      })
      // #endregion

      //#region fetchVehicleNotesReq
      .addCase(fetchVehicleNotesReq.pending, state => {
        state.vehicleNotesData = [];
        state.currentVehicleVin = '';
        state.currentVehicleType = '';
      })
      .addCase(fetchVehicleNotesReq.fulfilled, (state, action) => {
        const { data, payload } = action.payload;
        state.vehicleNotesData = data;
        state.currentVehicleVin = payload.veh_vin;
        state.currentVehicleType = payload.veh_type === 'N' ? 'new' : 'used';

        const { notesModal } = current(state);
        state.notesModal = !notesModal;
      })
      .addCase(fetchVehicleNotesReq.rejected, state => {
        state.vehicleNotesData = [];
        state.currentVehicleVin = '';
        state.currentVehicleType = '';
      })
      // #endregion

      //#region fetchReportRecipients
      .addCase(fetchReportRecipients.pending, state => {
        state.recipients = [];
      })
      .addCase(fetchReportRecipients.fulfilled, (state, action) => {
        state.recipients = action.payload || [];
        state.checkedStatus = state.recipients?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
        state.isAllSelected = true;
      })
      .addCase(fetchReportRecipients.rejected, state => {
        state.recipients = [];
      })
      // #endregion

      //#region fetchAgeBucketReportRecipients
      .addCase(fetchAgeBucketReportRecipients.pending, state => {
        state.recipients = [];
      })
      .addCase(fetchAgeBucketReportRecipients.fulfilled, (state, action) => {
        state.ageBucketRecipients = action.payload || [];
        state.checkedAgeBucketStatus = state.ageBucketRecipients?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
        state.isAllAgeBucketSelected = true;
      })
      .addCase(fetchAgeBucketReportRecipients.rejected, state => {
        state.recipients = [];
      })
      // #endregion fetchAgeBucketReportRecipients

      //#region fetchVDSReportRecipients
      .addCase(fetchVDSReportRecipients.pending, state => {
        state.vdsRecipients = [];
      })
      .addCase(fetchVDSReportRecipients.fulfilled, (state, action) => {
        state.vdsRecipients = action.payload || [];
        state.checkedVdsStatus = state.vdsRecipients?.reduce((acc, email) => {
          acc[email] = true;
          return acc;
        }, {});
        state.isAllVdsSelected = true;
      })
      .addCase(fetchVDSReportRecipients.rejected, state => {
        state.vdsRecipients = [];
      })
      // #endregion

      //#region fetchUpdateVehicleNoteReq
      .addCase(fetchUpdateVehicleNoteReq.fulfilled, state => {
        state.notesModal = false;
        state.currentVehicleVin = '';
        state.currentVehicleType = '';
      })
      // #endregion

      //#region fetchSearchInvDataReq
      .addCase(fetchSearchInvDataReq.pending, state => {
        state.searchInvData = [];
        state.isSearchInvLoading = true;
        state.isSearchInvDataLoaded = false;
      })
      .addCase(fetchSearchInvDataReq.fulfilled, (state, action) => {
        state.searchInvData = action.payload;
        state.isSearchInvLoading = false;
        state.isSearchInvDataLoaded = true;
      })
      .addCase(fetchSearchInvDataReq.rejected, state => {
        state.searchInvData = [];
        state.isSearchInvDataLoaded = false;
        state.isSearchInvLoading = false;
      })
      // #endregion

      //#region fetchDealerRatingReq
      .addCase(fetchDealerRatingReq.pending, state => {
        state.dealerRating = [];
        state.isdealerRatingLoading = true;
      })
      .addCase(fetchDealerRatingReq.fulfilled, (state, action) => {
        state.dealerRating = action.payload;
        state.isdealerRatingLoading = false;
      })
      .addCase(fetchDealerRatingReq.rejected, state => {
        state.dealerRating = [];
        state.isdealerRatingLoading = false;
      });
    // #endregion
  }
});

export const {
  handleIsRRStatus,
  handleChartData,
  handleTotalData,
  handleFilteredRecords,
  handleChartLoaded,
  handleTableLoaded,
  handleDataFilter,
  handleSubFilter,
  handleSearchBox,
  handleCurrentSearchValue,
  handleVdsTotal,
  handleCpoTotal,
  handleWindowstickerTotal,
  handleBuyersguideTotal,
  handleSetVdsFilter,
  handleCpoFilter,
  handleNotesFilter,
  handleWindowstickerFilter,
  handleBuyersguideFilter,
  handleTableSummary,
  handleNotesModal,
  handleVehicleNotesData,
  handleCurrentVehicleVin,
  handleCurrentVehicleType,
  handleViewer,
  handleIsOpenStickerModel,
  handleInvStickerData,
  handleStickerType,
  handleIsOpenGuideModel,
  handleGuideData,
  handleLabelType,
  handleIsOpenAddendumModel,
  handleToggleVdpTrendModal,
  handleIsOpenAiCommentModel,
  handleAddendumData,
  handleAiCommentData,
  handleIsOpenStickerHistoryModel,
  handleInvStickerHistoryData,
  handleIsOpenRatingModel,
  handleIsOpenCustWarrModel,
  handleCustWarrData,
  handleIsCustomWarrantyForm,
  handleDealerState,
  handleResetInvPage,
  handleDealerRating,
  handleBucketWidget,
  handleBucketRange,
  handleFilterReset,
  handleSortValue,
  updateRecipients,
  updateCheckedStatus,
  recipients,
  toggleSelectAll,
  updateVDSRecipients,
  updateVDSCheckedStatus,
  vdsRecipients,
  toggleVdsSelectAll,
  updateAgeBucketRecipients,
  updateAgeBucketCheckedStatus,
  ageBucketRecipients,
  toggleAgeBucketSelectAll,
  handleIsIPacketStatus
} = inventorySlice.actions;
export default inventorySlice.reducer;
